import React from 'react';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import FilterListIcon from '@mui/icons-material/FilterList';
import { QcIssueStatus } from '../../../interfaces/qcIssue.interface';
import { QC_STATUS_RGB } from './qcTheme';

// Header status tiles are filter toggles (order/labels per handoff); First Pass is admin-only; Pending uses the lighter header gray deliberately.
const TILE_ORDER: { status: QcIssueStatus; label: string }[] = [
  { status: 'first_pass', label: 'First Pass' },
  { status: 'pending_assessor_action', label: 'Pending Assessor' },
  { status: 'needs_update', label: 'Needs Update' },
  { status: 'ready_for_tpn_review', label: 'TPN Review' },
  { status: 'approved', label: 'Approved' },
];

const HEADER_TILE_RGB: Record<QcIssueStatus, string> = {
  ...QC_STATUS_RGB,
  pending_assessor_action: '128,134,144',
};

const TilesBlock = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '6px',
  alignSelf: 'center',
  padding: '14px 24px',
  // Left rule only — a right rule read as a stray line beside the Continue QC Review modal's close button.
  borderLeft: '1px solid rgba(0,0,0,0.12)',
});

const TilesHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#424242',
  letterSpacing: '0.2px',
});

const HintIcon = styled(FilterListIcon)({
  fontSize: '15px',
  color: 'rgba(0,0,0,0.32)',
});

const TilesRow = styled('div')({
  display: 'flex',
  gap: '8px',
});

const Tile = styled('button', {
  shouldForwardProp: prop => !['rgb', 'on', 'zero'].includes(prop as string),
})<{ rgb: string; on: boolean; zero: boolean }>(({ rgb, on, zero }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  minWidth: '104px',
  padding: '8px 12px',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  fontFamily: 'inherit',
  background: on ? `rgba(${rgb},0.18)` : `rgba(${rgb},0.06)`,
  color: `rgb(${rgb})`,
  border: `1px solid ${on ? `rgba(${rgb},0.7)` : `rgba(${rgb},0.14)`}`,
  opacity: !zero || on ? 1 : 0.7,
  '&:hover': {
    filter: 'brightness(0.94)',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
    opacity: 1,
  },
}));

const TileCount = styled('span')({
  fontSize: '1.15rem',
  fontWeight: 700,
  lineHeight: 1,
});

const TileLabel = styled('span')({
  fontSize: '0.66rem',
  fontWeight: 600,
  letterSpacing: '0.3px',
  textTransform: 'uppercase',
  textAlign: 'center',
  lineHeight: 1.2,
});

export interface QcStatusTilesProps {
  counts: Record<QcIssueStatus, number>;
  selected: QcIssueStatus[];
  showFirstPass: boolean;
  onToggle: (status: QcIssueStatus) => void;
}

export default function QcStatusTiles({ counts, selected, showFirstPass, onToggle }: QcStatusTilesProps): JSX.Element {
  const tiles = TILE_ORDER.filter(t => showFirstPass || t.status !== 'first_pass');
  const total = tiles.reduce((sum, t) => sum + counts[t.status], 0);
  return (
    <TilesBlock>
      <TilesHeader>
        QC Issues ({total})
        <Tooltip title='Click a status to filter'>
          <HintIcon />
        </Tooltip>
      </TilesHeader>
      <TilesRow>
        {tiles.map(tile => {
          const on = selected.includes(tile.status);
          return (
            <Tile
              key={tile.status}
              type='button'
              rgb={HEADER_TILE_RGB[tile.status]}
              on={on}
              zero={counts[tile.status] === 0}
              aria-pressed={on}
              onClick={() => onToggle(tile.status)}
            >
              <TileCount>{counts[tile.status]}</TileCount>
              <TileLabel>{tile.label}</TileLabel>
            </Tile>
          );
        })}
      </TilesRow>
    </TilesBlock>
  );
}
